import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet-draw';
import { mapService } from '../services/map.service';
import { isPointInPolygon } from '../utils/zone-calculator';

interface DrawingToolsProps {
  category: string;
}

export function DrawingTools({ category }: DrawingToolsProps) {
  const map = useMap();
  const activeHandlerRef = useRef<L.Draw.Feature | null>(null);

  useEffect(() => {
    const drawnItems = new L.FeatureGroup();
    map.addLayer(drawnItems);

    let markerLayer: L.Marker | null = null;
    let polygonLayer: L.Layer | null = null;

    const markerSub = mapService.geometry$.subscribe(({ takeoffPoint }) => {
      if (markerLayer) {
        drawnItems.removeLayer(markerLayer);
        markerLayer = null;
      }
      if (takeoffPoint) {
        markerLayer = L.marker([takeoffPoint.lat, takeoffPoint.lng]);
        drawnItems.addLayer(markerLayer);
      }
    });

    let additionalMarkerLayers: L.CircleMarker[] = [];

    const additionalSub = mapService.geometry$.subscribe(({ additionalTakeoffPoints }) => {
      additionalMarkerLayers.forEach(l => drawnItems.removeLayer(l));
      additionalMarkerLayers = additionalTakeoffPoints.map(p =>
        L.circleMarker([p.lat, p.lng], { radius: 8, color: '#38bdf8', fillColor: '#38bdf8', fillOpacity: 0.8 })
      );
      additionalMarkerLayers.forEach(l => drawnItems.addLayer(l));
    });

    const drawControl = new L.Control.Draw({
      edit: { featureGroup: drawnItems },
      draw: {
        // `title` is a leaflet-draw runtime option not reflected in @types/leaflet-draw
        marker: { title: 'Točka vzleta / Takeoff point' } as L.DrawOptions.MarkerOptions,
        polygon: { title: 'Področje letenja / Flight area', showArea: true } as L.DrawOptions.PolygonOptions,
        polyline: false,
        rectangle: false,
        circle: false,
        circlemarker: false,
      },
    });
    map.addControl(drawControl);

    function onCreated(e: L.LeafletEvent) {
      const event = e as L.DrawEvents.Created;
      if (event.layerType === 'marker') {
        const { lat, lng } = (event.layer as L.Marker).getLatLng();
        if (mapService.activeDrawingTool$.value === 'additional-marker') {
          const { flightArea } = mapService.geometry$.value;
          if (flightArea.length >= 3 && !isPointInPolygon({ lat, lng }, flightArea)) {
            mapService.setPolygonError(
              'Dodatna točka vzleta mora biti znotraj področja letenja / Additional takeoff point must be inside the flight area'
            );
            return;
          }
          mapService.addAdditionalTakeoffPoint({ lat, lng });
        } else {
          mapService.setTakeoffPoint({ lat, lng });
        }
      } else if (event.layerType === 'polygon') {
        const latLngs = (event.layer as L.Polygon).getLatLngs()[0] as L.LatLng[];
        const newArea = latLngs.map(ll => ({ lat: ll.lat, lng: ll.lng }));
        const { takeoffPoint, additionalTakeoffPoints } = mapService.geometry$.value;
        const allTakeoffPoints = takeoffPoint ? [takeoffPoint, ...additionalTakeoffPoints] : additionalTakeoffPoints;

        const outsidePoint = allTakeoffPoints.find(p => !isPointInPolygon(p, newArea));
        if (outsidePoint) {
          mapService.setPolygonError(
            'Vse točke vzleta morajo biti znotraj področja letenja / All takeoff points must be inside the flight area'
          );
          return;
        }

        if (polygonLayer) drawnItems.removeLayer(polygonLayer);
        polygonLayer = event.layer;
        if (!drawnItems.hasLayer(polygonLayer)) drawnItems.addLayer(polygonLayer);
        mapService.setPolygonError(null);
        mapService.setFlightArea(newArea);
      }
    }

    map.on(L.Draw.Event.CREATED, onCreated);

    return () => {
      markerSub.unsubscribe();
      additionalSub.unsubscribe();
      map.removeControl(drawControl);
      map.removeLayer(drawnItems);
      map.off(L.Draw.Event.CREATED, onCreated);
      // Intentional: reset geometry when tools are torn down (category change or unmount)
      mapService.reset();
    };
  }, [map, category]);

  useEffect(() => {
    function onDrawStop() {
      if (activeHandlerRef.current) {
        activeHandlerRef.current = null;
        mapService.setActiveDrawingTool(null);
      }
    }

    const sub = mapService.activeDrawingTool$.subscribe(tool => {
      activeHandlerRef.current?.disable();
      activeHandlerRef.current = null;
      if (tool === 'marker' || tool === 'additional-marker') {
        const handler = new L.Draw.Marker(map as unknown as L.DrawMap, {
          title: tool === 'marker' ? 'Točka vzleta / Takeoff point' : 'Dodatna točka vzleta / Additional takeoff point',
        } as L.DrawOptions.MarkerOptions);
        handler.enable();
        activeHandlerRef.current = handler;
      } else if (tool === 'polygon') {
        const handler = new L.Draw.Polygon(map as unknown as L.DrawMap, {
          title: 'Področje letenja / Flight area',
          showArea: true,
        } as L.DrawOptions.PolygonOptions);
        handler.enable();
        activeHandlerRef.current = handler;
      }
    });

    map.on(L.Draw.Event.DRAWSTOP, onDrawStop);

    return () => {
      sub.unsubscribe();
      activeHandlerRef.current?.disable();
      activeHandlerRef.current = null;
      map.off(L.Draw.Event.DRAWSTOP, onDrawStop);
    };
  }, [map]);

  return null;
}
